import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DollarSign, Plus, ArrowRight, FileText } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// دالة تنسيق المبالغ بالأرقام الإنجليزية وشعار الريال السعودي الجديد
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 ﷼';
  return `${Number(amount).toLocaleString('en-US')} ﷼`;
};

export default function Fees() {
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [cases, setCases] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    case_id: '',
    consultation_id: '',
    client_id: '',
    client_name: '',
    fee_type: 'amount',
    amount: '',
    percentage: '',
    total_amount: '',
    payment_method: 'نقدي',
    payment_status: 'pending',
    installments: [],
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [feesRes, casesRes, consultationsRes] = await Promise.all([
        axios.get(`${API}/fees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/cases`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/consultations`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setFees(feesRes.data);
      setCases(casesRes.data);
      setConsultations(consultationsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/fees`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      fetchData();
      setFormData({
        case_id: '',
        consultation_id: '',
        client_id: '',
        client_name: '',
        fee_type: 'amount',
        amount: '',
        percentage: '',
        total_amount: '',
        payment_method: 'نقدي',
        payment_status: 'pending',
        installments: [],
        notes: '',
      });
    } catch (err) {
      console.error('Error creating fee:', err);
    }
  };

  const getFeeTypeLabel = (type) => {
    const types = {
      amount: 'مبلغ ثابت',
      percentage: 'نسبة مئوية',
      both: 'مبلغ + نسبة',
    };
    return types[type] || type;
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowRight className="w-6 h-6 text-white" />
            </button>
            <Logo size="md" />
            <h1 className="text-2xl font-heading font-bold text-white">إدارة الأتعاب</h1>
          </div>
          <button
            data-testid="add-fee-btn"
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all duration-300"
          >
            <Plus className="w-5 h-5" />
            إضافة أتعاب
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fees.map((fee) => (
            <div
              key={fee.id}
              className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 hover-lift"
              data-testid={`fee-card-${fee.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <DollarSign className="w-8 h-8 text-gold" />
                <span
                  className={`px-3 py-1 rounded-full text-xs font-body ${
                    fee.payment_status === 'paid'
                      ? 'bg-green-100 text-green-700'
                      : fee.payment_status === 'pending'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {fee.payment_status === 'paid'
                    ? 'مدفوعة'
                    : fee.payment_status === 'pending'
                    ? 'معلقة'
                    : fee.payment_status === 'partial'
                    ? 'دفعة جزئية'
                    : fee.payment_status}
                </span>
              </div>
              <h3 className="text-lg font-heading font-semibold text-primary mb-2">
                {fee.client_name}
              </h3>
              <div className="space-y-1 mb-4">
                <p className="text-sm text-gray-600 font-body">
                  <strong>النوع:</strong> {getFeeTypeLabel(fee.fee_type)}
                </p>
                {fee.amount && (
                  <p className="text-sm text-gray-600 font-body">
                    <strong>المبلغ:</strong> {formatCurrency(fee.amount)}
                  </p>
                )}
                {fee.percentage && (
                  <p className="text-sm text-gray-600 font-body">
                    <strong>النسبة:</strong> {fee.percentage}%
                  </p>
                )}
                <p className="text-2xl font-bold text-gold font-heading">
                  {formatCurrency(fee.total_amount)}
                </p>
                <p className="text-sm text-gray-600 font-body">
                  <strong>طريقة السداد:</strong> {fee.payment_method}
                </p>
              </div>
              {fee.notes && (
                <p className="text-sm text-gray-500 font-body">{fee.notes}</p>
              )}
            </div>
          ))}
        </div>

        {fees.length === 0 && (
          <div className="text-center py-16" data-testid="no-fees">
            <DollarSign className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 font-body">لا توجد أتعاب مسجلة</p>
          </div>
        )}
      </main>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 my-8" data-testid="fee-modal">
            <h2 className="text-2xl font-heading font-bold text-primary mb-6">إضافة أتعاب جديدة</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  نوع الخدمة
                </label>
                <select
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.startsWith('case-')) {
                      const caseItem = cases.find((c) => c.id === value.replace('case-', ''));
                      if (caseItem) {
                        setFormData({
                          ...formData,
                          case_id: caseItem.id,
                          consultation_id: '',
                          client_id: caseItem.client_id,
                          client_name: caseItem.client_name,
                        });
                      }
                    } else if (value.startsWith('consult-')) {
                      const consultItem = consultations.find(
                        (c) => c.id === value.replace('consult-', '')
                      );
                      if (consultItem) {
                        setFormData({
                          ...formData,
                          case_id: '',
                          consultation_id: consultItem.id,
                          client_id: consultItem.client_id,
                          client_name: consultItem.client_name,
                        });
                      }
                    }
                  }}
                >
                  <option value="">اختر القضية أو الاستشارة</option>
                  <optgroup label="القضايا">
                    {cases.map((c) => (
                      <option key={c.id} value={`case-${c.id}`}>
                        {c.title} - {c.client_name}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="الاستشارات">
                    {consultations.map((c) => (
                      <option key={c.id} value={`consult-${c.id}`}>
                        {c.subject} - {c.client_name}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  نوع الأتعاب
                </label>
                <select
                  data-testid="fee-type"
                  value={formData.fee_type}
                  onChange={(e) => setFormData({ ...formData, fee_type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="amount">مبلغ ثابت</option>
                  <option value="percentage">نسبة مئوية</option>
                  <option value="both">مبلغ + نسبة</option>
                </select>
              </div>

              {(formData.fee_type === 'amount' || formData.fee_type === 'both') && (
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    المبلغ (ريال)
                  </label>
                  <input
                    data-testid="fee-amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>
              )}

              {(formData.fee_type === 'percentage' || formData.fee_type === 'both') && (
                <div>
                  <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                    النسبة المئوية (%)
                  </label>
                  <input
                    data-testid="fee-percentage"
                    type="number"
                    value={formData.percentage}
                    onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  إجمالي المبلغ (ريال)
                </label>
                <input
                  data-testid="fee-total"
                  type="number"
                  value={formData.total_amount}
                  onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  طريقة السداد
                </label>
                <select
                  data-testid="payment-method"
                  value={formData.payment_method}
                  onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                >
                  <option value="نقدي">نقدي</option>
                  <option value="شيك">شيك</option>
                  <option value="تحويل بنكي">تحويل بنكي</option>
                  <option value="بطاقة ائتمان">بطاقة ائتمان</option>
                  <option value="آجل">آجل</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-primary mb-2 text-right font-body">
                  ملاحظات
                </label>
                <textarea
                  data-testid="fee-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-gold outline-none font-body"
                  placeholder="ملاحظات إضافية..."
                />
              </div>

              <div className="flex gap-4 justify-end">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-all font-body"
                >
                  إلغاء
                </button>
                <button
                  data-testid="fee-submit"
                  type="submit"
                  className="px-6 py-2 bg-gold text-white rounded-md hover:bg-gold-dark transition-all font-body"
                >
                  حفظ
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
